<template>
  <div>
    <h2 class="title is-4">Minhas Licitações</h2>

    <div v-if="!data.length" class="panel">
      <div class="panel-block is-justify-content-center">
        <section class="section">
          <div class="content has-text-grey has-text-centered">
            <p>Nenhuma licitação</p>
          </div>
        </section>
      </div>
    </div>

    <div v-if="data.length" class="panel">
      <div class="panel-block">
        <b-table
          class="table is-fullwidth table-typography-size"
          :data="data"
          :loading="tableLoading"
        >
          <b-table-column sortable field="tipo" label="Tipo" v-slot="props">
            <b-tooltip
              :label="getBiddingTypeMeta(props.row.tipo).label"
              :position="mobile ? 'is-left' : 'is-top'"
            >
              <b-icon
                class="custom-icon"
                type="is-primary"
                :icon="getBiddingTypeMeta(props.row.tipo).icon"
              ></b-icon>
            </b-tooltip>
          </b-table-column>

          <b-table-column
            centered
            sortable
            field="participation"
            label="Exclusiva"
            v-slot="props"
            :visible="displayExclusiveColumn"
          >
            <b-icon
              icon="check"
              size="is-medium"
              type="is-success"
              v-show="displayExclusiveIcon(props.row)"
            />
          </b-table-column>

          <b-table-column centered sortable field="biddingNumber" label="Licitação" v-slot="props">
            <router-link
              :to="{
                name: getBiddingTypeMeta(props.row.tipo).routeName,
                params: { id: props.row.id },
              }"
            >
              {{ props.row.biddingNumber }}
            </router-link>
          </b-table-column>

          <b-table-column centered sortable field="situationName" label="Situação" v-slot="props">
            {{ props.row.situationName }}
          </b-table-column>

          <b-table-column
            centered
            sortable
            field="object"
            label="Objeto"
            v-slot="props"
            :visible="!tableLoading && data.some(row => row.object)"
          >
            {{ props.row.object }}
          </b-table-column>

          <b-table-column
            centered
            sortable
            field="openDate"
            label="Previsão de Abertura"
            v-slot="props"
            :visible="!tableLoading && data.some(row => row.openDate)"
          >
            <div
              style="display: flex; flex-wrap: nowrap"
              v-if="props.row.situationId === 17 && props.row.reopenDate"
            >
              {{ props.row.reopenDate | formatDate }}
              <p style="font-size: 0.85rem; color: #8b8b8b; margin-left: 5px">(Horário local)</p>
            </div>

            <div style="display: flex; flex-wrap: nowrap" v-else>
              {{ props.row.openDate | formatDate }}
              <p style="font-size: 0.85rem; color: #8b8b8b; margin-left: 5px">(Horário local)</p>
            </div>
          </b-table-column>

          <b-table-column
            centered
            label="Opções"
            v-slot="props"
            :visible="
              !tableLoading &&
              company.buyer &&
              (operateAuctionPermission || operateDispensationPermission) &&
              data.some(row => row.situationId < 4)
            "
          >
            <b-tooltip label="Editar">
              <button
                class="button is-primary"
                v-if="company.id === props.row.buyerId && props.row.situationId < 4"
                @click.prevent.stop="editBidding(props.row.id, props.row.tipo)"
              >
                <b-icon icon="pencil" size="is-small" />
              </button>
            </b-tooltip>
          </b-table-column>

          <template v-slot:footer>
            <AppPagination
              :data-filters="{
                page: filters.page,
                perPage: filters.perPage,
              }"
              :number-lines="10"
              :total="count"
              @change-page="page => changePage(page)"
              @change-per-page="perPage => changePerPage(perPage)"
            />
          </template>
        </b-table>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import AppPagination from '@/components/AppPagination';
import { formatarData as formatDate } from '@/utils/format';

export default {
  name: 'BuyerMyBiddings',
  components: {
    AppPagination,
  },
  props: {
    count: { type: Number, default: 0 },
    data: { type: Array, default: () => [] },
    mobile: { type: Boolean, default: false },
    operateAuctionPermission: { type: Boolean, default: false },
    operateDispensationPermission: { type: Boolean, default: false },
    tableLoading: { type: Boolean, default: false },
  },
  data() {
    const filters = {
      page: 1,
      perPage: 10,
    };

    return {
      filters,
    };
  },
  filters: {
    formatDate,
  },
  computed: {
    ...mapState('companies', ['company']),
    displayExclusiveColumn() {
      return this.data.some(
        row =>
          row.participation === 'AMPLOCOTA' ||
          row.participation === 'RESERVADO' ||
          row.participation === 'EXCLUSIVA',
      );
    },
  },
  methods: {
    getBiddingTypeMeta(tipo) {
      const normalizedType = (tipo || '').toUpperCase();
      const typeMap = {
        CONCORRENCIA: {
          icon: 'alpha-c-box',
          label: 'Concorrência',
          routeEditName: 'PregaoEditar',
          routeName: 'Pregao',
        },
        DISPENSA: {
          icon: 'alpha-d-box',
          label: 'Dispensa',
          routeEditName: 'PregaoEditar',
          routeName: 'Pregao',
        },
        PREGAO: {
          icon: 'alpha-p-box',
          label: 'Pregão',
          routeEditName: 'PregaoEditar',
          routeName: 'Pregao',
        },
      };

      return typeMap[normalizedType] || typeMap.PREGAO;
    },
    changePage(page) {
      this.filters.page = page;
      this.$emit('filter-buyer-biddings', {
        page: this.filters.page,
        perPage: this.filters.perPage,
      });
    },
    changePerPage(perPage) {
      this.filters.page = Number(perPage) > Number(this.filters.perPage) ? 1 : this.filters.page;
      this.filters.perPage = perPage;
      this.$emit('filter-buyer-biddings', {
        page: this.filters.page,
        perPage: this.filters.perPage,
      });
    },
    displayExclusiveIcon(row) {
      return (
        row.participation === 'AMPLOCOTA' ||
        row.participation === 'RESERVADO' ||
        row.participation === 'EXCLUSIVA'
      );
    },
    async editBidding(id, tipo) {
      this.$router.push({ name: this.getBiddingTypeMeta(tipo).routeEditName, params: { id } });
    },
  },
};
</script>

<style lang="scss" scoped>
.buyer-column {
  min-width: 150px;
}
</style>
